'use client'

import { useHydrated } from '@/hooks/common/use-hydrated'
import { useConsentManager, useTranslations } from '@c15t/nextjs'
import Link from 'next/link'
import { useEffect, useRef } from 'react'

export function CookieBanner() {
  const { hasConsented, saveConsents, setActiveUI } = useConsentManager()
  const translations = useTranslations()
  const hydrated = useHydrated()
  // hasConsented() đọc cookie — server không thấy được, nên phải gate qua
  // `hydrated` để render đầu tiên ở client khớp hệt SSR (đều coi là chưa
  // đồng ý), tránh hydration mismatch. Giá trị thật áp dụng ở render kế tiếp
  // ngay sau khi hydrate xong.
  const consented = hydrated && hasConsented()
  const bannerRef = useRef<HTMLDivElement>(null)

  // Lock body scroll while banner is active
  useEffect(() => {
    if (consented) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [consented])

  // Overlay chỉ chặn chuột — trap focus bàn phím trong banner để Tab không
  // lọt xuống nội dung phía sau. Bỏ qua ConsentDialog (<dialog>) của c15t
  // mở từ nút Customize, nó tự quản lý focus.
  useEffect(() => {
    if (!hydrated || consented) return
    const banner = bannerRef.current
    if (!banner) return

    const getFocusables = () =>
      Array.from(
        banner.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      )
    const isAllowed = (el: EventTarget | null) =>
      el instanceof Node &&
      (banner.contains(el) ||
        (el instanceof Element && !!el.closest('dialog, [role="dialog"]')))

    const onFocusIn = (e: FocusEvent) => {
      if (!isAllowed(e.target)) getFocusables()[0]?.focus()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !isAllowed(document.activeElement)) return
      if (!banner.contains(document.activeElement)) return
      const focusables = getFocusables()
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last?.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [hydrated, consented])

  if (consented) return null

  return (
    <>
      {/* Blocking overlay — sits below the banner, above all site content */}
      <div className='fixed inset-0 z-[9990] bg-black/50' />

      {/* Banner — above the overlay */}
      <div
        ref={bannerRef}
        className='fixed bottom-0 left-0 right-0 z-[9991] border-t border-gray-200 bg-white px-6 py-3 shadow-lg'>
        <div className='mx-auto flex items-center gap-3 md:container max-md:flex-col'>
          <div>
            <span className='font-semibold'>
              {translations.cookieBanner.title}
            </span>
            <div className='flex-1 text-sm text-gray-700'>
              {translations.cookieBanner.description}{' '}
              <Link
                href='/page/chinh-sach-cookie'
                className='text-blue-600 underline hover:text-blue-800'
              >
                Chính sách Cookie
              </Link>
            </div>
          </div>
          <div className='flex shrink-0 gap-2'>
            <button
              onClick={() => setActiveUI('dialog')}
              className='rounded border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100'
            >
              {translations.common.customize}
            </button>
            <button
              onClick={() => saveConsents('all')}
              className='rounded bg-black px-4 py-1.5 text-sm font-medium text-white hover:bg-gray-800'
            >
              {translations.common.acceptAll}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
